import { isReportIntentLandingPath } from "@/lib/report-intent-path";

export const SUPPORT_HELPER_EXACT_PATHS = new Set(["/contact"]);
export const SUPPORT_HELPER_PREFIXES: string[] = [];
export const SUPPORT_HELPER_HIDDEN_PREFIXES = ["/ai-astrologer", "/ai-kundli", "/cart"];

const AI_ASTROLOGER_EXACT_PATHS = new Set([
  "/ai-astrologer",
  "/free-astrology-chat",
  "/hi/muft-jyotish-chat",
]);
const AI_ASTROLOGER_PREFIXES = ["/chat"];

const GLOBAL_AGENT_ENTRY_HIDDEN_EXACT_PATHS = new Set([
  "/",
  "/about",
  "/account",
  "/cart",
  "/checkout",
  "/contact",
  "/free-kundali",
  "/get-app",
  "/login",
  "/notifications",
  "/privacy",
  "/terms",
  "/hi/privacy",
]);

const GLOBAL_AGENT_ENTRY_HIDDEN_PREFIXES = [
  "/api",
  "/auth",
  "/dashboard",
  "/admin",
  "/free-kundali/",
  "/get-app/",
  "/login/",
  "/privacy/",
  "/terms/",
];

export const STICKY_CHAT_HIDDEN_PREFIXES = [
  "/admin",
  "/free-kundali",
  "/ai-astrologer",
  "/hi/astrologers",
  "/hi/muft-jyotish-chat",
  "/auth",
  "/account",
  "/checkout",
  "/ops",
  "/reports",
];

// Home already renders InlineChatPreview + KundaliFormCard inline; the floating
// CTA at bottom-[76px] right-4 lands on top of the form's Generate button on a
// 375×812 viewport. Suppress on the exact `/` path while keeping the sticky for
// the rest of the SEO tail.
export const STICKY_CHAT_HIDDEN_EXACT = new Set(["/"]);

export function normalizeOverlayPath(pathname: string | null | undefined) {
  if (!pathname) return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

/**
 * The English twin of a Hindi route: strips a leading `/hi` segment only.
 * Overlay route rules are written against English paths; matching through this
 * keeps a Hindi twin from getting an overlay its English page is protected
 * from (c-ov-03).
 */
export function withoutLocalePrefix(path: string): string {
  return path === "/hi" ? "/" : path.replace(/^\/hi(?=\/)/, "");
}

function matchesRoutePolicy(path: string, exactPaths: Set<string>, prefixes: string[]) {
  return (
    exactPaths.has(path) ||
    prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
  );
}

/**
 * AI Jyotishi owns these onboarding and per-reading conversation surfaces.
 * Competing global prompts must defer to the native form/chat and its
 * contextual per-answer feedback controls.
 */
export function isAIAstrologerExperiencePath(pathname: string | null | undefined) {
  const path = normalizeOverlayPath(pathname);
  return matchesRoutePolicy(path, AI_ASTROLOGER_EXACT_PATHS, AI_ASTROLOGER_PREFIXES);
}

export function shouldShowGlobalAgentEntry(pathname: string | null | undefined) {
  const path = normalizeOverlayPath(pathname);
  return (
    !GLOBAL_AGENT_ENTRY_HIDDEN_EXACT_PATHS.has(path) &&
    !GLOBAL_AGENT_ENTRY_HIDDEN_PREFIXES.some((prefix) => path.startsWith(prefix)) &&
    !isAIAstrologerExperiencePath(path)
  );
}

function hasQueryParam(
  searchParams: URLSearchParams | ReadonlyURLSearchParamsLike | null | undefined,
  key: string
) {
  return Boolean(searchParams?.has(key));
}

export type ReadonlyURLSearchParamsLike = Pick<URLSearchParams, "has">;

export function shouldShowSupportHelper(
  pathname: string | null | undefined,
  searchParams?: URLSearchParams | ReadonlyURLSearchParamsLike | null
) {
  const path = normalizeOverlayPath(pathname);
  if (
    SUPPORT_HELPER_HIDDEN_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    )
  ) {
    return false;
  }

  const isSupportPath =
    SUPPORT_HELPER_EXACT_PATHS.has(path) ||
    SUPPORT_HELPER_PREFIXES.some((prefix) => path.startsWith(prefix));
  const isReportRecovery =
    path === "/free-kundali" &&
    (hasQueryParam(searchParams, "post_purchase_report") || hasQueryParam(searchParams, "order"));

  return isSupportPath || isReportRecovery;
}

export function shouldShowStickyChatButton(pathname: string | null | undefined) {
  const path = normalizeOverlayPath(pathname);
  if (STICKY_CHAT_HIDDEN_EXACT.has(path)) return false;
  if (isReportIntentLandingPath(path)) return false;
  // Prefixes match the Hindi twin too (/hi/free-kundali renders the same inline
  // chat as /free-kundali). The exact `/` rule stays English-only: the Hindi
  // home has no inline form under the pill.
  const english = withoutLocalePrefix(path);
  return !STICKY_CHAT_HIDDEN_PREFIXES.some((prefix) =>
    [path, english].some((candidate) => candidate === prefix || candidate.startsWith(`${prefix}/`))
  );
}

function isReportConversionPath(path: string) {
  return isReportIntentLandingPath(path) || path === "/reports" || path.startsWith("/reports/");
}

export function shouldShowMobileBottomBar(pathname: string | null | undefined) {
  const path = normalizeOverlayPath(pathname);
  return path !== "/admin" && !path.startsWith("/admin/") && !isReportConversionPath(path);
}

export function shouldShowFloatingFeedback(pathname: string | null | undefined) {
  const path = normalizeOverlayPath(pathname);
  return (
    path !== "/admin" &&
    !path.startsWith("/admin/") &&
    path !== "/cart" &&
    !path.startsWith("/cart/") &&
    !isReportConversionPath(path) &&
    !isAIAstrologerExperiencePath(path)
  );
}
